import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveRole,
  useCreateRole,
  useRolesPaginated,
  useUpdateRole,
} from "@/hooks/useRoles";
import { useActionsCatalog } from "@/hooks/useActions";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import { RolePayload, RoleType } from "@/domain/rol.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import RoleFilters, { Filters } from "@/ui/content/roles/Filters";
import RoleModal from "@/ui/modals/roles/RoleModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  type: "",
  subType: "",
};

const TYPE_LABELS: Record<string, string> = {
  STANDARD_SYSTEM_ROLE: "Standard system",
  STANDARD_ACCOUNT_ROLE: "Standard account",
  CUSTOM_ROLE: "Custom",
};

const SUBTYPE_LABELS: Record<string, string> = {
  "IN-HOUSE_PROVIDER": "In-house provider",
  SUPPORT_STAFF: "Support staff",
  OFFICER: "Officer",
};

const RolesPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    role?: RoleType;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    role?: RoleType;
  }>({ open: false });

  const {
    rolesPaginated,
    isLoadingRolesPaginated,
    isFetchingRolesPaginated,
    refetchRolesPaginated,
  } = useRolesPaginated({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
    type: filters.type,
    subType: filters.subType,
  });

  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const archiveRole = useArchiveRole();
  const createAuditLog = useCreateAuditlog();

  const { actionsCatalog } = useActionsCatalog();

  const actionNameById = useMemo(() => {
    const map = new Map<string, string>();
    (actionsCatalog?.list || []).forEach((a) => {
      if (a._id) map.set(a._id, a.name || a._id);
    });
    return map;
  }, [actionsCatalog]);

  const isSaving = createRole.isPending || updateRole.isPending;

  useEffect(() => {
    setPageTitle("Roles");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ROLES,
    option: OPTIONS.ROLES_LIST,
    actionCode: ACTION_CODE.WEB_ROLE_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of roles",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchRolesPaginated();
  }, [
    debouncedSearch,
    filters.status,
    filters.type,
    filters.subType,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ROLES,
        option: OPTIONS.ROLES_LIST,
        actionCode: ACTION_CODE.WEB_ROLE_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of roles",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, role: undefined });
  };

  const handleOpenEdit = (role: RoleType) => {
    setModalState({ open: true, role });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, role: undefined });
  };

  const handleSave = async (data: RolePayload) => {
    const editing = modalState.role;

    try {
      if (editing) {
        await updateRole.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ROLES,
          option: OPTIONS.ROLE_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ROLE_EDIT,
          action: "When editing a role",
          detail: `Edited role ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The role has been updated.", "success");
      } else {
        await createRole.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ROLES,
          option: OPTIONS.ROLE_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ROLE_ADD,
          action: "When creating a role",
          detail: `Created role ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The role has been created.", "success");
      }

      handleCloseModal();
      refetchRolesPaginated();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.role;
    if (!target) return;

    try {
      await archiveRole.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ROLES,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_ROLE_DELETE,
        action: "When deleting a role",
        detail: `Deleted role ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchRolesPaginated();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "200px",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "180px",
    },
    {
      id: "subType",
      isSortable: true,
      disablePadding: false,
      label: "Sub type",
      align: "left",
      width: "160px",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!rolesPaginated || rolesPaginated.list.length === 0) return [];

    return rolesPaginated.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, role: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const actionIds: string[] = Array.isArray(row.actions) ? row.actions : [];
      const actionNames = actionIds
        .map((id) => actionNameById.get(id) || id)
        .filter(Boolean);

      return createDataRow(index, {
        name: <div>{row.name}</div>,
        type: <div>{TYPE_LABELS[row.type] || row.type || "-"}</div>,
        subType: <div>{SUBTYPE_LABELS[row.subType] || row.subType || "-"}</div>,
        actions:
          actionNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {actionNames.map((name, i) => (
                <Chip
                  key={`${row._id}-action-${i}`}
                  size="small"
                  label={<div className="text-xs">{name}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [rolesPaginated, theme, actionNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Roles
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <RoleFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={rolesPaginated?.totalCount}
                  isLoading={
                    isLoadingRolesPaginated || isFetchingRolesPaginated
                  }
                  messageEmptyData="No roles to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <RoleModal
          open={modalState.open}
          onClose={handleCloseModal}
          role={modalState.role}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveRole.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default RolesPage;
